import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Flex,
  Text,
  Title,
  Button,
  TextInput,
  NumberInput,
  Select,
  SelectItem,
  Badge,
  Callout,
  Divider,
} from "@tremor/react";
import {
  BeakerIcon,
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import MqttContext from "../../../services/ws/MqttContext";

const COMMON_TYPES = [
  { value: "temp", label: "Temperature", unit: "c" },
  { value: "humidity", label: "Humidity", unit: "%" },
  { value: "pressure", label: "Pressure", unit: "hPa" },
  { value: "light", label: "Light", unit: "lux" },
  { value: "voltage", label: "Voltage", unit: "v" },
  { value: "current", label: "Current", unit: "a" },
  { value: "power", label: "Power", unit: "w" },
  { value: "distance", label: "Distance", unit: "m" },
  { value: "gps", label: "GPS", unit: "coords" },
  { value: "motion", label: "Motion", unit: "bool" },
  { value: "co2", label: "CO2", unit: "ppm" },
  { value: "custom", label: "Custom", unit: "" },
];

export default function DeviceSimulatorDrawer({ isOpen, onClose, device }) {
  const mqttClient = useContext(MqttContext);
  const [metrics, setMetrics] = useState([
    { type: "temp", unit: "c", value: 22.5, channel: 1 },
  ]);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showApiDetails, setShowApiDetails] = useState(false);
  const [showCodeExport, setShowCodeExport] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("arduino");
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    // Load existing capabilities as templates if device has any
    if (device.capabilities && device.capabilities.length > 0) {
      const existingMetrics = device.capabilities
        .filter(cap => cap.type !== 'gps' && cap.type !== 'text')
        .map(cap => ({
          type: cap.type || 'custom',
          unit: cap.unit || '',
          value: typeof cap.value === 'number' ? cap.value : 0,
          channel: parseInt(cap.channel) || 1,
        }));
      
      if (existingMetrics.length > 0) {
        setMetrics(existingMetrics);
      }
    }
  }, [device.capabilities, isOpen]);

  const addMetric = () => {
    const newChannel = Math.max(...metrics.map(m => m.channel)) + 1;
    setMetrics([
      ...metrics,
      { type: "temp", unit: "c", value: 0, channel: newChannel },
    ]);
  };

  const removeMetric = (index) => {
    setMetrics(metrics.filter((_, i) => i !== index));
  };

  const updateMetric = (index, field, value) => {
    const newMetrics = [...metrics];
    newMetrics[index][field] = value;
    
    // Auto-set unit when type changes
    if (field === "type" && value !== "custom") {
      const typeConfig = COMMON_TYPES.find(t => t.value === value);
      if (typeConfig) {
        newMetrics[index].unit = typeConfig.unit;
      }
    }
    
    setMetrics(newMetrics);
  };

  const generateRandomValue = (type) => {
    switch (type) {
      case "temp":
        return (20 + Math.random() * 10).toFixed(1);
      case "humidity":
        return (40 + Math.random() * 40).toFixed(0);
      case "pressure":
        return (1000 + Math.random() * 50).toFixed(0);
      case "light":
        return (100 + Math.random() * 900).toFixed(0);
      case "voltage":
        return (3.0 + Math.random() * 0.5).toFixed(2);
      case "motion":
        return Math.random() > 0.5 ? 1 : 0;
      case "co2":
        return (400 + Math.random() * 600).toFixed(0);
      default:
        return (Math.random() * 100).toFixed(2);
    }
  };

  const randomizeValue = (index) => {
    const metric = metrics[index];
    const randomValue = generateRandomValue(metric.type);
    updateMetric(index, "value", parseFloat(randomValue));
  };

  const getMqttTopic = () => {
    if (!mqttClient) return "";
    return `v1/${mqttClient.options.username}/things/${device.serial}/data/json`;
  };

  const getPayload = () => {
    return metrics.map(m => ({
      type: m.type,
      unit: m.unit,
      value: parseFloat(m.value),
      channel: m.channel,
    }));
  };

  const sendData = async () => {
    if (!mqttClient || !mqttClient.connected) {
      setError("MQTT client not connected");
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare data in the expected format
      const payload = getPayload();

      // Publish to the device's data topic
      const topic = getMqttTopic();
      
      await mqttClient.publish(topic, JSON.stringify(payload));
      
      setLastSent(new Date());
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error sending data:", err);
      setError(err.message || "Failed to send data");
    } finally {
      setSending(false);
    }
  };

  const generateCode = (language) => {
    const topic = getMqttTopic() || "v1/{username}/things/{device_serial}/data/json";
    const payload = getPayload();
    const brokerUrl = "wss://mqtt.zafron.dev:8084/mqtt";
    
    switch (language) {
      case "arduino":
        const cayenneChannels = payload.map((metric, index) => {
          const channelNum = metric.channel;
          let cayenneFunction = "";
          
          switch (metric.type) {
            case "temp":
              cayenneFunction = `Cayenne.celsiusWrite(${channelNum}, ${metric.value});`;
              break;
            case "humidity":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_RELATIVE_HUMIDITY, UNIT_PERCENT);`;
              break;
            case "pressure":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_BAROMETRIC_PRESSURE, UNIT_HECTOPASCAL);`;
              break;
            case "light":
              cayenneFunction = `Cayenne.luxWrite(${channelNum}, ${metric.value});`;
              break;
            case "voltage":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_VOLTAGE, UNIT_VOLTS);`;
              break;
            case "distance":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_PROXIMITY, UNIT_METER);`;
              break;
            default:
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value});`;
          }
          
          return { channelNum, cayenneFunction, comment: `// Send ${metric.type} data` };
        });

        return `#define CAYENNE_PRINT Serial
#include <CayenneMQTTEthernet.h> // Change this to use a different communication device

// Cayenne authentication info. This should be obtained from the Cayenne Dashboard.
char username[] = "{your_username}";
char password[] = "{your_password}";
char clientID[] = "${device.serial || '{your_device_serial}'}";

void setup()
{
    Serial.begin(9600);
    Cayenne.begin(username, password, clientID);
}

void loop()
{
    Cayenne.loop();
}

// This function is called at intervals to send sensor data to Cayenne.
CAYENNE_OUT_DEFAULT()
{
    ${cayenneChannels.map(ch => `${ch.comment}\n    ${ch.cayenneFunction}`).join('\n    ')}
}`;

      case "esp32":
        const esp32Channels = payload.map((metric, index) => {
          const channelNum = metric.channel;
          let cayenneFunction = "";
          
          switch (metric.type) {
            case "temp":
              cayenneFunction = `Cayenne.celsiusWrite(${channelNum}, ${metric.value});`;
              break;
            case "humidity":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_RELATIVE_HUMIDITY, UNIT_PERCENT);`;
              break;
            case "pressure":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_BAROMETRIC_PRESSURE, UNIT_HECTOPASCAL);`;
              break;
            case "light":
              cayenneFunction = `Cayenne.luxWrite(${channelNum}, ${metric.value});`;
              break;
            case "voltage":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_VOLTAGE, UNIT_VOLTS);`;
              break;
            case "distance":
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value}, TYPE_PROXIMITY, UNIT_METER);`;
              break;
            default:
              cayenneFunction = `Cayenne.virtualWrite(${channelNum}, ${metric.value});`;
          }
          
          return { channelNum, cayenneFunction, comment: `// Send ${metric.type} data` };
        });

        return `// This example shows how to connect to Cayenne using an ESP32 and send/receive sample data.
// Make sure you install the ESP32 Board Package and select the correct ESP32 board before compiling.
// To install the ESP32 Board Package follow the instructions here: https://github.com/espressif/arduino-esp32/blob/master/README.md#installation-instructions.

//#define CAYENNE_DEBUG
#define CAYENNE_PRINT Serial
#include <CayenneMQTTESP32.h>

// WiFi network info.
char ssid[] = "your_wifi_ssid";
char wifiPassword[] = "your_wifi_password";

// Cayenne authentication info. This should be obtained from the Cayenne Dashboard.
char username[] = "{your_username}";
char password[] = "{your_password}";
char clientID[] = "${device.serial || '{your_device_serial}'}";

unsigned long lastMillis = 0;

void setup() {
    Serial.begin(9600);
    Cayenne.begin(username, password, clientID, ssid, wifiPassword);
}

void loop() {
    Cayenne.loop();
}

// Default function for sending sensor data at intervals to Cayenne.
// You can also use functions for specific channels, e.g CAYENNE_OUT(1) for sending channel 1 data.
CAYENNE_OUT_DEFAULT()
{
    ${esp32Channels.map(ch => `${ch.comment}\n    ${ch.cayenneFunction}`).join('\n    ')}
}

// Default function for processing actuator commands from the Cayenne Dashboard.
// You can also use functions for specific channels, e.g CAYENNE_IN(1) for channel 1 commands.
CAYENNE_IN_DEFAULT()
{
    CAYENNE_LOG("Channel %u, value %s", request.channel, getValue.asString());
    //Process message here. If there is an error set an error message using getValue.setError(), e.g getValue.setError("Error message");
}`;

      case "javascript":
        return `// JavaScript (Node.js with mqtt package)
const mqtt = require('mqtt');

const client = mqtt.connect('${brokerUrl}', {
  username: '{your_username}',
  password: '{your_password}'
});

client.on('connect', () => {
  console.log('Connected to MQTT broker');
  
  const data = ${JSON.stringify(payload, null, 2)};
  
  client.publish('${topic}', JSON.stringify(data), (err) => {
    if (err) {
      console.error('Publish error:', err);
    } else {
      console.log('Data sent successfully');
    }
    client.end();
  });
});`;

      case "python":
        return `# Python (with paho-mqtt package)
import json
import paho.mqtt.client as mqtt

def on_connect(client, userdata, flags, rc):
    print(f"Connected with result code {rc}")
    
    data = ${JSON.stringify(payload, null, 2)}
    
    client.publish("${topic}", json.dumps(data))
    print("Data sent successfully")
    client.disconnect()

client = mqtt.Client()
client.username_pw_set("{your_username}", "{your_password}")
client.on_connect = on_connect

client.connect("mqtt.zafron.dev", 1883, 60)
client.loop_forever()`;

      case "go":
        return `// Go (with paho.mqtt.golang package)
package main

import (
    "encoding/json"
    "fmt"
    "log"
    "time"
    
    MQTT "github.com/eclipse/paho.mqtt.golang"
)

type Metric struct {
    Type    string  \`json:"type"\`
    Unit    string  \`json:"unit"\`
    Value   float64 \`json:"value"\`
    Channel int     \`json:"channel"\`
}

func main() {
    opts := MQTT.NewClientOptions()
    opts.AddBroker("ssl://mqtt.zafron.dev:1883")
    opts.SetUsername("{your_username}")
    opts.SetPassword("{your_password}")

    client := MQTT.NewClient(opts)
    if token := client.Connect(); token.Wait() && token.Error() != nil {
        log.Fatal(token.Error())
    }

    data := []Metric${JSON.stringify(payload).replace(/"type"/g, 'Type').replace(/"unit"/g, 'Unit').replace(/"value"/g, 'Value').replace(/"channel"/g, 'Channel')}

    payload, _ := json.Marshal(data)
    token := client.Publish("${topic}", 0, false, payload)
    token.Wait()
    
    fmt.Println("Data sent successfully")
    client.Disconnect(250)
}`;

      case "rust":
        return `// Rust (with paho-mqtt crate)
use paho_mqtt as mqtt;
use serde_json::json;

fn main() {
    let create_opts = mqtt::CreateOptionsBuilder::new()
        .server_uri("ssl://mqtt.zafron.dev:1883")
        .finalize();

    let mut cli = mqtt::Client::new(create_opts).unwrap();

    let conn_opts = mqtt::ConnectOptionsBuilder::new()
        .user_name("{your_username}")
        .password("{your_password}")
        .finalize();

    cli.connect(conn_opts).unwrap();

    let data = json!(${JSON.stringify(payload, null, 4)});

    let msg = mqtt::MessageBuilder::new()
        .topic("${topic}")
        .payload(data.to_string())
        .qos(0)
        .finalize();

    cli.publish(msg).unwrap();
    println!("Data sent successfully");

    cli.disconnect(None).unwrap();
}`;

      case "cpp":
        return `// C++ (with paho-mqtt-cpp library)
#include <iostream>
#include <string>
#include "mqtt/client.h"

int main() {
    const std::string SERVER_ADDRESS("tcp://mqtt.zafron.dev:1883");
    const std::string CLIENT_ID("device_simulator");
    const std::string TOPIC("${topic}");

    mqtt::client client(SERVER_ADDRESS, CLIENT_ID);

    mqtt::connect_options connOpts;
    connOpts.set_user_name("{your_username}");
    connOpts.set_password("{your_password}");

    try {
        client.connect(connOpts);
        std::cout << "Connected" << std::endl;

        std::string payload = R"(${JSON.stringify(payload)})";
        
        mqtt::message_ptr pubmsg = mqtt::make_message(TOPIC, payload);
        pubmsg->set_qos(0);
        
        client.publish(pubmsg);
        std::cout << "Data sent successfully" << std::endl;

        client.disconnect();
    }
    catch (const mqtt::exception& exc) {
        std::cerr << "Error: " << exc.what() << std::endl;
        return 1;
    }

    return 0;
}`;

      default:
        return "// Language not supported";
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-25 transition-opacity z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } ${isCollapsed ? 'w-16' : 'w-96 lg:w-[500px]'}`}>
        
        {/* Collapse/Expand Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white border border-gray-200 rounded-l-md p-2 shadow-md hover:bg-gray-50 z-10"
        >
          {isCollapsed ? (
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          )}
        </button>

        {isCollapsed ? (
          /* Collapsed State */
          <div className="p-4 h-full flex flex-col items-center justify-center">
            <BeakerIcon className="h-6 w-6 text-indigo-500 mb-2" />
            <Text className="text-xs text-center transform rotate-90 whitespace-nowrap">
              Simulator
            </Text>
          </div>
        ) : (
          /* Expanded State */
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="border-b p-4">
              <Flex justifyContent="between" alignItems="start">
                <div className="flex-1">
                  <Flex className="gap-2" alignItems="center" justifyContent="start">
                    <BeakerIcon className="h-5 w-5 text-indigo-500" />
                    <Title className="!text-left text-lg">Simulator</Title>
                  </Flex>
                  <Text className="text-sm mt-1">
                    Send data and watch it appear
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </Flex>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {error && (
                <Callout color="red" title="Error" className="text-sm">
                  {error}
                </Callout>
              )}

              {success && (
                <Callout color="green" title="Success!" className="text-sm">
                  Data sent! Check the overview page.
                </Callout>
              )}

              {/* Metrics Configuration */}
              <div className="space-y-4">
                {metrics.map((metric, index) => (
                  <Card key={index} className="p-3">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <Select
                            value={metric.type}
                            onValueChange={(value) => updateMetric(index, "type", value)}
                          >
                            {COMMON_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Channel
                          </label>
                          <NumberInput
                            value={metric.channel}
                            onChange={(e) => updateMetric(index, "channel", parseInt(e.target.value) || 1)}
                            min={1}
                            max={99}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Unit
                          </label>
                          <TextInput
                            value={metric.unit}
                            onChange={(e) => updateMetric(index, "unit", e.target.value)}
                            placeholder="unit"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Value
                          </label>
                          <NumberInput
                            value={metric.value}
                            onChange={(e) => updateMetric(index, "value", parseFloat(e.target.value) || 0)}
                            step="0.01"
                          />
                        </div>
                      </div>

                      <Flex className="gap-2">
                        <Button
                          size="xs"
                          variant="secondary"
                          onClick={() => randomizeValue(index)}
                          className="flex-1"
                        >
                          Random
                        </Button>
                        {metrics.length > 1 && (
                          <Button
                            size="xs"
                            variant="secondary"
                            color="red"
                            icon={TrashIcon}
                            onClick={() => removeMetric(index)}
                          />
                        )}
                      </Flex>
                    </div>
                  </Card>
                ))}

                <Flex className="gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={PlusIcon}
                    onClick={addMetric}
                    className="flex-1"
                  >
                    Add Metric
                  </Button>
                </Flex>

                <Button
                  size="sm"
                  variant="primary"
                  icon={PaperAirplaneIcon}
                  onClick={sendData}
                  loading={sending}
                  className="w-full"
                >
                  Send Data
                </Button>

                {lastSent && (
                  <Text className="text-xs text-gray-500 text-center">
                    Last sent: {lastSent.toLocaleTimeString()}
                  </Text>
                )}

                <Flex className="gap-2">
                  <Button
                    size="sm"
                    variant="light"
                    icon={InformationCircleIcon}
                    onClick={() => setShowApiDetails(!showApiDetails)}
                    className="flex-1"
                  >
                    {showApiDetails ? 'Hide' : 'Show'} API
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    icon={CodeBracketIcon}
                    onClick={() => setShowCodeExport(!showCodeExport)}
                    className="flex-1"
                  >
                    {showCodeExport ? 'Hide' : 'Show'} Code
                  </Button>
                </Flex>

                {/* API Details (Collapsible) */}
                {showApiDetails && (
                  <>
                    <Divider />
                    <div className="space-y-3">
                      <Text className="font-medium text-sm">MQTT API Details</Text>

                      <div>
                        <Text className="text-xs font-medium mb-1">Topic:</Text>
                        <pre className="bg-gray-900 text-gray-100 p-2 rounded text-xs overflow-x-auto">
                          <code>{getMqttTopic() || "v1/{username}/things/{serial}/data/json"}</code>
                        </pre>
                      </div>

                      <div>
                        <Text className="text-xs font-medium mb-1">Payload:</Text>
                        <pre className="bg-gray-900 text-gray-100 p-2 rounded text-xs overflow-x-auto">
                          <code>{JSON.stringify(getPayload(), null, 2)}</code>
                        </pre>
                      </div>
                    </div>
                  </>
                )}

                {/* Code Export (Collapsible) */}
                {showCodeExport && (
                  <>
                    <Divider />
                    <div className="space-y-3">
                      <Text className="font-medium text-sm">Code Examples</Text>
                      
                      <div>
                        <Text className="text-xs font-medium mb-1">Language:</Text>
                        <Select
                          value={selectedLanguage}
                          onValueChange={setSelectedLanguage}
                        >
                          <SelectItem value="arduino">Arduino (Ethernet)</SelectItem>
                          <SelectItem value="esp32">ESP32 (WiFi)</SelectItem>
                          <SelectItem value="javascript">JavaScript</SelectItem>
                          <SelectItem value="python">Python</SelectItem>
                          <SelectItem value="go">Go</SelectItem>
                          <SelectItem value="rust">Rust</SelectItem>
                          <SelectItem value="cpp">C++</SelectItem>
                        </Select>
                      </div>

                      <div>
                        <Text className="text-xs font-medium mb-1">
                          {selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)} Code:
                        </Text>
                        <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
                          <code>{generateCode(selectedLanguage)}</code>
                        </pre>
                      </div>
                      
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => navigator.clipboard.writeText(generateCode(selectedLanguage))}
                        className="w-full"
                      >
                        Copy Code
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}